/**
 * QiltTrendSparkline — QILT SES "Overall Quality of Educational
 * Experience" trend line for an AU uni.
 *
 * Always renders a national line (2017-2023, 7 years). If we have
 * per-uni data (at least 2 years, e.g. 2022 + 2023 for the ~10
 * unis named in QILT 2022 Table 6 + 2023 Figure 5), overlays the
 * uni-specific line so the reader can see both curves at once.
 *
 * Mirrors NssTrendSparkline's inline-SVG design so it slots into
 * the same slots on Compare / Shortlist / UniversityDetail.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingDown, TrendingUp, Minus } from "lucide-react";

export default function QiltTrendSparkline({ uniId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!uniId) return;
    api.get(`/methodology/qilt-ses/trend?uni_id=${uniId}`)
       .then((r) => setData(r.data))
       .catch(() => setData(null));
  }, [uniId]);

  if (!data?.national || data.national.length < 2) return null;

  const nat = data.national;
  const uni = data.uni || [];
  // Domain across both series.
  const allVals = [
    ...nat.map((p) => p.positivity_pct),
    ...uni.map((p) => p.positivity_pct),
  ];
  const min = Math.min(...allVals) - 3;
  const max = Math.max(...allVals) + 3;
  const range = max - min || 1;
  const W = 110;
  const H = 24;

  // National line always spans 2017-2023 (7 years).
  const natPts = nat.map((p, i) => {
    const x = (i / (nat.length - 1)) * W;
    const y = H - ((p.positivity_pct - min) / range) * H;
    return [x, y, p];
  });
  const natPath = natPts
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");

  // Uni line: map by cycle onto the same x-axis so 2022+2023 land on
  // the correct national years.
  const yearIndex = Object.fromEntries(nat.map((p, i) => [p.cycle, i]));
  const uniPts = uni
    .filter((p) => yearIndex[p.cycle] !== undefined)
    .map((p) => {
      const x = (yearIndex[p.cycle] / (nat.length - 1)) * W;
      const y = H - ((p.positivity_pct - min) / range) * H;
      return [x, y, p];
    });
  const uniPath = uniPts.length >= 2
    ? uniPts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ")
    : null;

  // Delta label — use per-uni if we have it, else national.
  const trendSeries = uni.length >= 2 ? uni : nat;
  const first = trendSeries[0].positivity_pct;
  const last = trendSeries[trendSeries.length - 1].positivity_pct;
  const delta = Math.round((last - first) * 10) / 10;
  const Icon = delta > 0.5 ? TrendingUp : delta < -0.5 ? TrendingDown : Minus;
  const color = delta > 0.5 ? "text-emerald-700" : delta < -0.5 ? "text-red-700" : "text-stone-500";

  const spanLabel = uni.length >= 2
    ? `QILT ${trendSeries[0].cycle}→${trendSeries[trendSeries.length - 1].cycle}`
    : `AU nat'l QILT ${nat[0].cycle}→${nat[nat.length - 1].cycle}`;

  const tooltip = [
    "National · " + nat.map((p) => `${p.cycle} ${p.positivity_pct}%`).join(" · "),
    uni.length ? "This uni · " + uni.map((p) => `${p.cycle} ${p.positivity_pct}%`).join(" · ") : null,
  ].filter(Boolean).join("  |  ");

  return (
    <div
      className="mt-1.5 inline-flex items-center gap-2 text-[11px] font-mono"
      data-testid="qilt-trend-sparkline"
      title={tooltip}
    >
      <svg width={W} height={H} className="shrink-0">
        {/* National line — green, thinner, drawn first */}
        <path d={natPath} fill="none" stroke="#059669" strokeWidth={1.2} strokeOpacity={0.55} />
        {natPts.map(([x, y], i) => (
          <circle key={`n${i}`} cx={x} cy={y} r={1.5} fill="#059669" fillOpacity={0.55} />
        ))}
        {/* Per-uni line — purple, thicker, drawn on top */}
        {uniPath && (
          <>
            <path d={uniPath} fill="none" stroke="#4C1D95" strokeWidth={1.8} />
            {uniPts.map(([x, y], i) => (
              <circle key={`u${i}`} cx={x} cy={y} r={2.2}
                      fill={i === uniPts.length - 1 ? "#4C1D95" : "#7c3aed"} />
            ))}
          </>
        )}
      </svg>
      <span className={`inline-flex items-center gap-1 ${color}`}>
        <Icon className="h-3 w-3" />
        {delta >= 0 ? "+" : ""}{delta} pts ({spanLabel})
      </span>
    </div>
  );
}
